import React from "react";
import { i18n } from "@lingui/core";
import { SemanticFilter, SemanticFilterItem } from "src/types/SemanticFilter";
import { WIcon } from "@warp-ds/components/react/icon";
import { Pill, Toggle } from "@warp-ds/react";

export type StandardFilterProps = {
	filter: SemanticFilter;
	index: number;
	selected?: unknown;
	onChange?: (next: unknown) => void;
	onPathChange?: (depth: number, currentLabel?: string, backLabel?: string) => void;
	onBackChange?: (handler?: () => void) => void;
	focusValue?: unknown;
};

export function StandardFilter({
	filter,
	index,
	selected,
	onChange,
	onPathChange,
	onBackChange,
	focusValue,
}: StandardFilterProps) {
	const options: { label: React.ReactNode; value: unknown }[] = [];
	const defaultSelected: { label: React.ReactNode; value: unknown }[] = [];
	const hasSelected = Array.isArray(selected);
	const selectedValues = hasSelected ? (selected as unknown[]) : [];

	const hasNested = (filter.filter_items ?? []).some(
		(item: SemanticFilterItem) => Array.isArray(item.filter_items) && item.filter_items.length > 0,
	);

	(filter.filter_items ?? []).forEach((item: SemanticFilterItem) => {
		const option = {
			label: item.display_name ?? item.name ?? String(item.value ?? ""),
			value: item.value ?? item.name ?? item.display_name ?? "",
		};
		options.push(option);
		if (item.selected) {
			defaultSelected.push(option);
		}
	});

	if (hasNested) {
		const effectiveSelected = hasSelected ? selectedValues : defaultSelected.map((opt) => opt.value);
		const selectedSet = new Set(effectiveSelected);
		const [path, setPath] = React.useState<
			Array<{ label: string; items: NonNullable<SemanticFilter["filter_items"]>; value: unknown }>
		>([]);

		const currentItems = path.length === 0 ? (filter.filter_items ?? []) : path[path.length - 1].items;

		type PathEntry = { label: string; items: NonNullable<SemanticFilter["filter_items"]>; value: unknown };

		const findPathToValue = React.useCallback(
			(items: NonNullable<SemanticFilter["filter_items"]>, value: unknown): PathEntry[] | undefined => {
				for (const item of items) {
					const label = item.display_name ?? item.name ?? i18n.t({ id: "explore.filter.fallback", message: "Filter" });
					const candidate = item.value ?? item.name ?? item.display_name;
					const children = (item.filter_items ?? []) as NonNullable<SemanticFilter["filter_items"]>;
					const hasChildren = Array.isArray(children) && children.length > 0;

					if (candidate === value) {
						return hasChildren ? [{ label, items: children, value: candidate }] : [];
					}

					if (hasChildren) {
						const subPath = findPathToValue(children, value);
						if (subPath) {
							return [{ label, items: children, value: candidate }, ...subPath];
						}
					}
				}
				return undefined;
			},
			[],
		);

		const goInto = (item: NonNullable<SemanticFilter["filter_items"]>[number]) => {
			const children = (item.filter_items ?? []) as NonNullable<SemanticFilter["filter_items"]>;
			setPath((prev) => [
				...prev,
				{
					label: item.display_name ?? item.name ?? i18n.t({ id: "explore.filter.fallback", message: "Filter" }),
					items: children,
					value: getItemValue(item),
				},
			]);
		};

		const goBack = React.useCallback(() => {
			setPath((prev) => prev.slice(0, -1));
		}, []);

		const getItemLabel = (item: NonNullable<SemanticFilter["filter_items"]>[number]) =>
			item.display_name ?? item.name ?? String(item.value ?? "");

		const getItemValue = (item: NonNullable<SemanticFilter["filter_items"]>[number]) =>
			item.value ?? item.name ?? item.display_name ?? "";

		type SelectionEntry = { value: unknown; label: string };

		const collectSelectedEntries = (items: SemanticFilterItem[]): SelectionEntry[] => {
			const entries: SelectionEntry[] = [];
			items.forEach((item: SemanticFilterItem) => {
				const value = getItemValue(item);
				if (selectedSet.has(value)) {
					entries.push({ value, label: getItemLabel(item) });
				}
				const children = (item.filter_items ?? []) as NonNullable<SemanticFilter["filter_items"]>;
				if (children.length > 0) {
					entries.push(...collectSelectedEntries(children));
				}
			});
			return entries;
		};

		const allLabel = i18n.t({
			id: "explore.filters.all",
			message: "All",
		});

		const collectSelectableValues = (items: SemanticFilterItem[]): unknown[] => {
			const values: unknown[] = [];
			items.forEach((item: SemanticFilterItem) => {
				const children = (item.filter_items ?? []) as NonNullable<SemanticFilter["filter_items"]>;
				if (children.length > 0) {
					values.push(...collectSelectableValues(children));
				} else {
					values.push(getItemValue(item));
				}
			});
			return values;
		};

		const collectSubtreeValues = (items: SemanticFilterItem[]): unknown[] => {
			const values: unknown[] = [];
			items.forEach((item: SemanticFilterItem) => {
				const value = getItemValue(item);
				if (value !== undefined) {
					values.push(value);
				}
				const children = (item.filter_items ?? []) as NonNullable<SemanticFilter["filter_items"]>;
				if (children.length > 0) {
					values.push(...collectSubtreeValues(children));
				}
			});
			return values;
		};

		const levelSubtreeValues = React.useMemo(
			() => Array.from(new Set(collectSubtreeValues(currentItems))),
			[currentItems],
		);
		const levelSelectionValues = React.useMemo(() => {
			const values: unknown[] = [];
			currentItems.forEach((item: SemanticFilterItem) => {
				const value = getItemValue(item);
				if (value !== undefined) {
					values.push(value);
				}
			});
			return Array.from(new Set(values));
		}, [currentItems]);
		const isItemFullySelected = React.useCallback(
			(item: SemanticFilterItem) => {
				const value = getItemValue(item);
				const children: SemanticFilterItem[] = item.filter_items ?? [];
				if (children.length === 0) {
					return typeof value !== "undefined" && selectedSet.has(value);
				}
				if (typeof value !== "undefined" && selectedSet.has(value)) {
					return true;
				}
				const leafValues = collectSelectableValues(children);
				return leafValues.length > 0 && leafValues.every((leafValue) => selectedSet.has(leafValue));
			},
			[selectedSet, collectSelectableValues],
		);
		const currentParentValue = path.length > 0 ? path[path.length - 1].value : undefined;
		const useParentSelection = path.length >= 1;
		const parentSelectedForLevel =
			useParentSelection && typeof currentParentValue !== "undefined" && selectedSet.has(currentParentValue);
		const allSelectedAtLevel =
			parentSelectedForLevel ||
			(currentItems.length > 0 && currentItems.every((item: SemanticFilterItem) => isItemFullySelected(item)));
		const toggleAllAtLevel = (nextChecked: boolean) => {
			const currentSelections = Array.isArray(effectiveSelected) ? [...effectiveSelected] : [];
			const selectionSet = new Set(currentSelections);

			if (nextChecked) {
				levelSubtreeValues.forEach((value) => selectionSet.delete(value));
				if (useParentSelection && typeof currentParentValue !== "undefined") {
					selectionSet.add(currentParentValue);
				} else {
					levelSelectionValues.forEach((value) => selectionSet.add(value));
				}
			} else {
				levelSubtreeValues.forEach((value) => selectionSet.delete(value));
				if (typeof currentParentValue !== "undefined") {
					selectionSet.delete(currentParentValue);
				}
			}

			onChange?.(Array.from(selectionSet));
		};

		const focusOnValue = (value: unknown) => {
			const items = (filter.filter_items ?? []) as NonNullable<SemanticFilter["filter_items"]>;
			const nextPath = findPathToValue(items, value);
			if (nextPath) {
				setPath(nextPath);
			}
		};

		const renderChildPills = (
			item: NonNullable<SemanticFilter["filter_items"]>[number],
			options?: { parentOnly?: boolean },
		) => {
			const children = (item.filter_items ?? []) as NonNullable<SemanticFilter["filter_items"]>;
			if (children.length === 0) return null;
			const parentValue = getItemValue(item);
			const parentSelected = typeof parentValue !== "undefined" && selectedSet.has(parentValue);
			const entries = collectSelectedEntries(children);
			if (options?.parentOnly) {
				if (!parentSelected && entries.length === 0) return null;
				const parentLabel = getItemLabel(item);
				return (
					<div className="flex items-center gap-4 ml-8 overflow-hidden">
						<div
							onClick={(event) => event.stopPropagation()}
							onMouseDown={(event) => event.stopPropagation()}
							className="flex"
							data-filter-pill
						>
							<Pill
								suggestion
								className="max-w-[200px]"
								canClose
								onClick={() => focusOnValue(parentValue)}
								onClose={() => {
									const currentSelections = Array.isArray(effectiveSelected) ? [...effectiveSelected] : [];
									const selectionSet = new Set(currentSelections);
									const subtreeValues = collectSubtreeValues([item] as NonNullable<SemanticFilter["filter_items"]>);
									subtreeValues.forEach((value) => selectionSet.delete(value));
									onChange?.(Array.from(selectionSet));
								}}
								label={<span className="inline-block max-w-[160px] truncate align-middle">{parentLabel}</span>}
							/>
						</div>
					</div>
				);
			}
			if (parentSelected) {
				const parentLabel = getItemLabel(item);
				return (
					<div className="flex items-center gap-4 ml-8 overflow-hidden">
						<div
							onClick={(event) => event.stopPropagation()}
							onMouseDown={(event) => event.stopPropagation()}
							className="flex"
							data-filter-pill
						>
							<Pill
								suggestion
								className="max-w-[200px]"
								canClose
								onClick={() => focusOnValue(parentValue)}
								onClose={() => toggleValue(parentValue, false)}
								label={<span className="inline-block max-w-[160px] truncate align-middle">{parentLabel}</span>}
							/>
						</div>
					</div>
				);
			}
			if (entries.length === 0) return null;
			const visibleEntries = entries.slice(0, 1);
			const remaining = entries.length - visibleEntries.length;
			return (
				<div className="flex items-center gap-4 ml-8 overflow-hidden">
					{visibleEntries.map((entry, idx) => (
						<div
							key={`${String(entry.value)}-${idx}`}
							onClick={(event) => event.stopPropagation()}
							onMouseDown={(event) => event.stopPropagation()}
							className="flex"
							data-filter-pill
						>
							<Pill
								suggestion
								className="max-w-[200px]"
								canClose
								onClick={() => focusOnValue(entry.value)}
								onClose={() => toggleValue(entry.value, false)}
								label={<span className="inline-block max-w-[160px] truncate align-middle">{entry.label}</span>}
							/>
						</div>
					))}
					{remaining > 0 && (
						<div
							onClick={(event) => event.stopPropagation()}
							onMouseDown={(event) => event.stopPropagation()}
							className="flex"
							data-filter-pill
						>
							<Pill suggestion className="max-w-[64px]" canClose={false} label={`+${remaining}`} />
						</div>
					)}
				</div>
			);
		};

		const toggleValue = (value: unknown, nextChecked: boolean) => {
			let nextValues = Array.isArray(effectiveSelected) ? [...effectiveSelected] : [];

			if (parentSelectedForLevel) {
				const selectionSet = new Set(nextValues);
				if (typeof currentParentValue !== "undefined") {
					selectionSet.delete(currentParentValue);
				}
				levelSelectionValues.forEach((levelValue) => selectionSet.add(levelValue));
				if (!nextChecked) {
					selectionSet.delete(value);
				}
				onChange?.(Array.from(selectionSet));
				return;
			}

			const exists = nextValues.some((v) => v === value);
			if (nextChecked) {
				if (!exists) {
					nextValues.push(value);
				}
			} else if (exists) {
				nextValues = nextValues.filter((v) => v !== value);
			}
			onChange?.(nextValues);
		};

		const isTopLevel = path.length === 0;

		React.useEffect(() => {
			if (typeof focusValue === "undefined") return;
			const items = (filter.filter_items ?? []) as NonNullable<SemanticFilter["filter_items"]>;
			const nextPath = findPathToValue(items, focusValue);
			if (nextPath) {
				setPath(nextPath);
			}
		}, [filter.filter_items, findPathToValue, focusValue]);

		React.useEffect(() => {
			const currentLabel = path.length > 0 ? path[path.length - 1].label : undefined;
			const backLabel =
				path.length > 1 ? path[path.length - 2].label : (filter.display_name ?? filter.name ?? "Filter");
			onPathChange?.(path.length, currentLabel, backLabel);
		}, [filter.display_name, filter.name, onPathChange, path]);

		React.useEffect(() => {
			if (!onBackChange) return;
			if (path.length > 0) {
				onBackChange(() => goBack());
			} else {
				onBackChange(undefined);
			}
		}, [onBackChange, path.length, goBack]);

		if (isTopLevel) {
			return (
				<div key={filter.name ?? filter.display_name ?? index} className="rounded-12 overflow-hidden">
					{(currentItems as SemanticFilterItem[]).map((item: SemanticFilterItem, idx: number) => {
						const showDivider = idx !== currentItems.length - 1;
						const value = item.value ?? item.name ?? item.display_name ?? "";
						const hasChildren = Array.isArray(item.filter_items) && item.filter_items.length > 0;
						const isSelected = effectiveSelected.includes(value);
						if (!hasChildren) {
							return (
								<div
									key={value as string}
									className={`standard-filter-toggle w-full ${showDivider ? "border-b border-border-default" : ""}`}
								>
									<Toggle
										type="checkbox"
										label={item.display_name ?? item.name ?? String(value)}
										checked={isSelected}
										onChange={(nextChecked) => toggleValue(value, Boolean(nextChecked))}
									/>
								</div>
							);
						}
						return (
							<button
								key={value as string}
								type="button"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									if (hasChildren) {
										goInto(item);
									}
								}}
								className={`standard-filter-row w-full py-14 text-left bg-transparent appearance-none ${
									showDivider ? "border-b border-border-default" : ""
								}`}
							>
								<div className="flex items-center w-full gap-8">
									<span className="text-body">{item.display_name ?? item.name ?? String(value)}</span>
									{renderChildPills(item, { parentOnly: true })}
									<WIcon
										name="ChevronRight"
										size="small"
										locale={i18n.locale}
										className="ml-auto shrink-0"
										aria-hidden="true"
									/>
								</div>
							</button>
						);
					})}
				</div>
			);
		}

		return (
			<div key={filter.name ?? filter.display_name ?? index} className="rounded-12 overflow-hidden">
				<div
					className={`standard-filter-toggle w-full ${currentItems.length > 0 ? "border-b border-border-default" : ""}`}
				>
					<Toggle
						type="checkbox"
						label={allLabel}
						checked={allSelectedAtLevel}
						onChange={(nextChecked) => toggleAllAtLevel(Boolean(nextChecked))}
					/>
				</div>
				{(currentItems as SemanticFilterItem[]).map((item: SemanticFilterItem, idx: number) => {
					const showDivider = idx !== currentItems.length - 1;
					const value = item.value ?? item.name ?? item.display_name ?? "";
					const isSelected = effectiveSelected.includes(value) || parentSelectedForLevel;
					const hasChildren = Array.isArray(item.filter_items) && item.filter_items.length > 0;

					if (hasChildren) {
						return (
							<button
								key={value as string}
								type="button"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									goInto(item);
								}}
								className={`standard-filter-row w-full py-14 text-left bg-transparent appearance-none ${
									showDivider ? "border-b border-border-default" : ""
								}`}
							>
								<div className="flex items-center w-full gap-8">
									<span className="text-body">{item.display_name ?? item.name ?? String(value)}</span>
									{renderChildPills(item)}
									<WIcon
										name="ChevronRight"
										size="small"
										locale={i18n.locale}
										className="ml-auto shrink-0"
										aria-hidden="true"
									/>
								</div>
							</button>
						);
					}

					return (
						<div
							key={value as string}
							className={`standard-filter-toggle w-full ${showDivider ? "border-b border-border-default" : ""}`}
						>
							<Toggle
								type="checkbox"
								label={item.display_name ?? item.name ?? String(value)}
								checked={isSelected}
								onChange={(nextChecked) => toggleValue(value, Boolean(nextChecked))}
							/>
						</div>
					);
				})}
			</div>
		);
	}

	const toggleSingleValue = (value: unknown, nextChecked: boolean) => {
		let nextValues = Array.isArray(selectedValues) ? [...selectedValues] : [];
		const exists = nextValues.some((v) => v === value);
		if (nextChecked) {
			if (!exists) {
				nextValues.push(value);
			}
		} else if (exists) {
			nextValues = nextValues.filter((v) => v !== value);
		}
		onChange?.(nextValues);
	};

	const items: SemanticFilterItem[] = filter.filter_items ?? [];

	return (
		<div key={filter.name ?? filter.display_name ?? index} className="rounded-12 overflow-hidden">
			{items.map((item: SemanticFilterItem, idx: number) => {
				const showDivider = idx !== items.length - 1;
				const value = item.value ?? item.name ?? item.display_name ?? "";
				const isSelected = selectedValues.includes(value);
				return (
					<div
						key={value as string}
						className={`standard-filter-toggle w-full ${showDivider ? "border-b border-border-default" : ""}`}
					>
						<Toggle
							type="checkbox"
							label={item.display_name ?? item.name ?? String(value)}
							checked={isSelected}
							onChange={(nextChecked) => toggleSingleValue(value, Boolean(nextChecked))}
						/>
					</div>
				);
			})}
		</div>
	);
}
